import { AfterViewInit, Component, ElementRef, Renderer2 } from '@angular/core';

@Component({
  selector: 'app-robot-thinking-face',
  templateUrl: './robot-thinking-face.component.html',
  standalone: true,
  styleUrls: ['./robot-thinking-face.component.scss', '../../../global-workspace-styles.scss']
})
export class RobotThinkingFaceComponent implements AfterViewInit {
  // Number of random floating background blocks to create
  private readonly FLOATING_BLOCK_COUNT = 12;

  // Max random negative delay for floating block animation (in seconds)
  private readonly MAX_ANIMATION_DELAY_SECONDS = 12;

  // The total duration of the main "thought" animation cycle (in milliseconds)
  private readonly ANIMATION_DURATION_MS = 6000;

  // The delay before starting the next cycle after one finishes (in milliseconds)
  private readonly RECYCLE_DELAY_MS = 500;

  // The fixed width offset for centering the main-doc object (42px * 2)
  private readonly CENTER_OFFSET_PX = 42;

  // The initial starting position (off-screen left) and ending position (off-screen right) offset
  private readonly OFF_SCREEN_OFFSET_PX = 150;

  // Percentage of the total duration for the entry phase (0.2 = 20%)
  private readonly ENTRY_PHASE_RATIO = 0.3;

  // Percentage of the total duration for the exit phase (0.5 = 50% - this implies the "pause" phase is 30%)
  private readonly EXIT_PHASE_RATIO = 0.6;

  // Range around the center where the 'thinking' class is applied (60px * 2)
  private readonly THINKING_ACTIVATION_RANGE_PX = 60;

  constructor(
    private renderer: Renderer2,
    private hostElement: ElementRef
  ) {}

  ngAfterViewInit(): void {
    const containerElement: HTMLElement = this.hostElement.nativeElement;
    this.createRandomFloatingBlocks(containerElement);
    this.startMainDocumentAnimationCycle(containerElement);
  }

  private createRandomFloatingBlocks(container: HTMLElement): void {
    for (let i = 0; i < this.FLOATING_BLOCK_COUNT; i++) {
      const backgroundBlock = this.renderer.createElement('div');
      this.renderer.addClass(backgroundBlock, 'bg-doc');

      // Set a random vertical position (0% to 100%)
      this.renderer.setStyle(backgroundBlock, 'top', Math.random() * 100 + '%');

      this.renderer.setStyle(
        backgroundBlock,
        'animationDelay',
        Math.random() * -this.MAX_ANIMATION_DELAY_SECONDS + 's'
      );

      this.renderer.appendChild(container, backgroundBlock);
    }
  }

  private startMainDocumentAnimationCycle(container: HTMLElement): void {
    const createThoughtDocument = () => {
      const mainThoughtDocument = this.renderer.createElement('div');
      this.renderer.addClass(mainThoughtDocument, 'main-doc');
      this.renderer.appendChild(container, mainThoughtDocument);

      const parentWidth = container.clientWidth;

      const centerLeftPosition = parentWidth / 2 - this.CENTER_OFFSET_PX;

      let animationStartTime: number | null = null;

      const animateThoughtMovement = (timestamp: number) => {
        if (!animationStartTime) animationStartTime = timestamp;
        const elapsedTime = timestamp - animationStartTime;
        let documentLeftPosition: number;

        const entryPhaseDuration = this.ANIMATION_DURATION_MS * this.ENTRY_PHASE_RATIO;
        const pausePhaseEndDuration =
          this.ANIMATION_DURATION_MS * (this.ENTRY_PHASE_RATIO + (this.EXIT_PHASE_RATIO - this.ENTRY_PHASE_RATIO));

        if (elapsedTime < entryPhaseDuration) {
          // 1. Entry Phase (Moves from off-screen left to centerLeftPosition)
          const totalEntryDistance = centerLeftPosition + this.OFF_SCREEN_OFFSET_PX;
          documentLeftPosition = -this.OFF_SCREEN_OFFSET_PX + totalEntryDistance * (elapsedTime / entryPhaseDuration);
        } else if (elapsedTime < pausePhaseEndDuration) {
          // 2. Pause/Hold Phase (Stays at centerLeftPosition)
          documentLeftPosition = centerLeftPosition;
        } else {
          // 3. Exit Phase (Moves from centerLeftPosition to off-screen right)
          const exitPhaseTime = elapsedTime - pausePhaseEndDuration;
          const exitPhaseDuration = this.ANIMATION_DURATION_MS - pausePhaseEndDuration;
          const totalExitDistance = parentWidth - centerLeftPosition + this.OFF_SCREEN_OFFSET_PX;

          documentLeftPosition = centerLeftPosition + totalExitDistance * (exitPhaseTime / exitPhaseDuration);
        }

        this.renderer.setStyle(mainThoughtDocument, 'left', documentLeftPosition + 'px');

        const isThinking =
          documentLeftPosition >= centerLeftPosition - this.THINKING_ACTIVATION_RANGE_PX &&
          documentLeftPosition <= centerLeftPosition + this.THINKING_ACTIVATION_RANGE_PX;

        if (isThinking) {
          this.renderer.addClass(container, 'thinking');
        } else {
          this.renderer.removeClass(container, 'thinking');
        }

        if (elapsedTime < this.ANIMATION_DURATION_MS) {
          requestAnimationFrame(animateThoughtMovement);
        } else {
          this.renderer.removeChild(container, mainThoughtDocument);
          setTimeout(createThoughtDocument, this.RECYCLE_DELAY_MS);
        }
      };

      requestAnimationFrame(animateThoughtMovement);
    };

    createThoughtDocument();
  }
}
